// ═══════════════════════════════════════════════════════════════════════════════
// WIZARD ABRA - Onboarding Complet pour les Pros
// Configuration conversationnelle de tous les paramètres boutique
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Wand2, Building2, MapPin, Phone, Clock, Shield, Image, Globe,
  Send, ChevronRight, Check, Loader2, Sparkles, ArrowLeft, X,
  Briefcase, Euro, FileText, Camera, Mail, Instagram, Facebook,
  Linkedin, Star, Target, Percent, Award, Zap, PartyPopper, Rocket,
  Upload, Eye, ExternalLink, ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES ÉTAPES DU WIZARD
// ═══════════════════════════════════════════════════════════════════════════════

const WIZARD_STEPS = [
  {
    id: 'welcome',
    category: 'intro',
    icon: Sparkles,
    color: 'from-amber-400 to-orange-500',
    title: 'Bienvenue',
    questions: [
      {
        id: 'intro',
        text: "Salut ! Je suis ton super assistant. Tu peux me donner le nom que tu veux ! Pour l'instant, je vais t'aider à créer ta vitrine pro en quelques minutes. C'est parti ?",
        type: 'confirm',
        field: null
      }
    ]
  },
  {
    id: 'identity',
    category: 'entreprise',
    icon: Building2,
    color: 'from-blue-500 to-indigo-600',
    title: 'Ton entreprise',
    questions: [
      {
        id: 'company_name',
        text: "Comment s'appelle ton entreprise ? (Ce sera le nom affiché sur ta boutique)",
        type: 'text',
        field: 'name',
        placeholder: 'Ex: Dupont Plomberie, Maison Martin...',
        validate: (v) => v.length >= 2,
        errorMsg: "Le nom doit faire au moins 2 caractères."
      },
      {
        id: 'slogan',
        text: "Super ! As-tu un slogan ou une accroche ? (Optionnel, tape 'non' pour passer)",
        type: 'text',
        field: 'slogan',
        optional: true,
        placeholder: 'Ex: La qualité à votre service depuis 2010',
        parse: (v) => v.toLowerCase() === 'non' ? '' : v
      },
      {
        id: 'metier',
        text: "Quel est ton métier principal ?",
        type: 'choice',
        field: 'metier_principal',
        choices: [
          'Plombier', 'Électricien', 'Maçon', 'Peintre', 'Carreleur',
          'Menuisier', 'Couvreur', 'Chauffagiste', 'Climaticien', 'Serrurier',
          'Architecte', 'Décorateur', 'Autre'
        ]
      },
      {
        id: 'siret',
        text: "Ton numéro SIRET ? (14 chiffres, ou tape 'plus tard' pour passer)",
        type: 'text',
        field: 'siret',
        optional: true,
        placeholder: '12345678901234',
        parse: (v) => v.toLowerCase().includes('tard') ? '' : v.replace(/\s/g, ''),
        validate: (v) => v === '' || v.length === 14,
        errorMsg: "Le SIRET doit contenir 14 chiffres."
      }
    ]
  },
  {
    id: 'visuals',
    category: 'visuels',
    icon: Camera,
    color: 'from-amber-500 to-orange-600',
    title: 'Ton logo',
    questions: [
      {
        id: 'logo_upload',
        text: "Ajoute le logo de ton entreprise ! Il apparaîtra sur ta boutique et tes devis PDF. (Tu peux aussi passer cette étape)",
        type: 'image_upload',
        field: 'logo_url',
        optional: true
      }
    ]
  },
  {
    id: 'location',
    category: 'coordonnées',
    icon: MapPin,
    color: 'from-emerald-500 to-teal-600',
    title: 'Tes coordonnées',
    questions: [
      {
        id: 'address',
        text: "Quelle est l'adresse de ton entreprise ?",
        type: 'text',
        field: 'address',
        placeholder: 'Ex: 15 rue des Artisans',
        validate: (v) => v.length >= 5,
        errorMsg: "L'adresse semble trop courte."
      },
      {
        id: 'city',
        text: "Dans quelle ville ?",
        type: 'text',
        field: 'city',
        placeholder: 'Ex: Lyon',
        validate: (v) => v.length >= 2,
        errorMsg: "Nom de ville trop court."
      },
      {
        id: 'postal_code',
        text: "Et le code postal ?",
        type: 'text',
        field: 'postal_code',
        placeholder: '69000',
        validate: (v) => /^\d{5}$/.test(v),
        errorMsg: "Le code postal doit contenir 5 chiffres."
      },
      {
        id: 'phone',
        text: "Ton numéro de téléphone professionnel ?",
        type: 'text',
        field: 'phone',
        placeholder: '06 12 34 56 78',
        validate: (v) => v.replace(/\s/g, '').length >= 10,
        errorMsg: "Numéro de téléphone invalide."
      },
      {
        id: 'email',
        text: "Ton email professionnel ?",
        type: 'text',
        field: 'email_pro',
        placeholder: 'contact@monentreprise.fr',
        validate: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        errorMsg: "Format d'email invalide."
      },
      {
        id: 'zone',
        text: "Dans quel rayon interviens-tu autour de ta ville ? (en km)",
        type: 'choice',
        field: 'zone_intervention_km',
        choices: ['10 km', '25 km', '50 km', '100 km', 'Toute la France'],
        parse: (v) => v === 'Toute la France' ? 999 : parseInt(v)
      }
    ]
  },
  {
    id: 'hours',
    category: 'horaires',
    icon: Clock,
    color: 'from-violet-500 to-purple-600',
    title: 'Tes horaires',
    questions: [
      {
        id: 'hours_type',
        text: "Comment fonctionnent tes horaires ?",
        type: 'choice',
        field: 'hours_type',
        choices: [
          'Classique (Lun-Ven 8h-18h)',
          'Étendu (Lun-Sam 7h-20h)',
          'Sur rendez-vous uniquement',
          'Personnalisé'
        ]
      },
      {
        id: 'urgency',
        text: "Proposes-tu des interventions d'urgence ?",
        type: 'choice',
        field: 'urgency_available',
        choices: ['Oui, 24h/24', 'Oui, en journée', 'Non']
      }
    ]
  },
  {
    id: 'services',
    category: 'services',
    icon: Briefcase,
    color: 'from-orange-500 to-red-500',
    title: 'Tes services',
    questions: [
      {
        id: 'services_main',
        text: "Quels sont tes 3 services principaux ? (Sépare-les par des virgules)",
        type: 'text',
        field: 'services_main',
        placeholder: 'Ex: Dépannage, Installation, Rénovation',
        parse: (v) => v.split(',').map(s => s.trim()).filter(s => s.length > 0)
      },
      {
        id: 'devis_gratuit',
        text: "Proposes-tu des devis gratuits ?",
        type: 'choice',
        field: 'devis_gratuit',
        choices: ['Oui, toujours', 'Oui, sous conditions', 'Non']
      }
    ]
  },
  {
    id: 'legal',
    category: 'mentions',
    icon: Shield,
    color: 'from-slate-600 to-slate-800',
    title: 'Mentions légales',
    questions: [
      {
        id: 'deposit',
        text: "Quel pourcentage d'acompte demandes-tu généralement ?",
        type: 'choice',
        field: 'devis_deposit_percent',
        choices: ['0%', '20%', '30%', '40%', '50%'],
        parse: (v) => parseInt(v)
      },
      {
        id: 'payment',
        text: "Comment tes clients règlent-ils le solde ?",
        type: 'choice',
        field: 'devis_payment_terms',
        choices: [
          'À la fin des travaux',
          'À 30 jours de la facture',
          '50% à mi-chantier, 50% à la fin',
          'En plusieurs fois'
        ]
      },
      {
        id: 'warranty',
        text: "As-tu une assurance décennale ou RC Pro ? (Précise le nom de l'assureur)",
        type: 'text',
        field: 'devis_warranty_text',
        placeholder: 'Ex: Décennale AXA n°123456',
        optional: true
      },
      {
        id: 'certifications',
        text: "As-tu des certifications ? (RGE, Qualibat, etc.)",
        type: 'text',
        field: 'certifications_text',
        placeholder: 'Ex: RGE QualiPAC, Qualibat',
        optional: true,
        parse: (v) => v.toLowerCase() === 'non' ? '' : v
      }
    ]
  },
  {
    id: 'social',
    category: 'réseaux',
    icon: Globe,
    color: 'from-amber-500 to-orange-600',
    title: 'Réseaux sociaux',
    questions: [
      {
        id: 'website',
        text: "As-tu un site web ? (Tape 'non' si tu n'en as pas)",
        type: 'text',
        field: 'website',
        placeholder: 'www.monsite.fr',
        optional: true,
        parse: (v) => v.toLowerCase() === 'non' ? '' : v
      },
      {
        id: 'instagram',
        text: "Ton Instagram ? (Juste le nom d'utilisateur, ou 'non')",
        type: 'text',
        field: 'instagram',
        placeholder: '@monentreprise',
        optional: true,
        parse: (v) => v.toLowerCase() === 'non' ? '' : v.replace('@', '')
      },
      {
        id: 'facebook',
        text: "Ta page Facebook ? (URL ou nom, ou 'non')",
        type: 'text',
        field: 'facebook',
        placeholder: 'facebook.com/monentreprise',
        optional: true,
        parse: (v) => v.toLowerCase() === 'non' ? '' : v
      }
    ]
  },
  {
    id: 'finish',
    category: 'fin',
    icon: PartyPopper,
    color: 'from-emerald-400 to-green-500',
    title: 'Terminé !',
    questions: [
      {
        id: 'complete',
        text: "Félicitations ! Ta boutique est configurée. Voici le récapitulatif de tes informations.",
        type: 'summary',
        field: null
      }
    ]
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function WizardAbraOnboarding({ onComplete, onSkip }) {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Données collectées
  const [collectedData, setCollectedData] = useState({
    name: '',
    slogan: '',
    metier_principal: '',
    siret: '',
    logo_url: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email_pro: '',
    zone_intervention_km: 50,
    hours_type: '',
    urgency_available: '',
    services_main: [],
    devis_gratuit: '',
    devis_deposit_percent: 30,
    devis_payment_terms: '',
    devis_warranty_text: '',
    certifications_text: '',
    website: '',
    instagram: '',
    facebook: ''
  });
  
  const currentStep = WIZARD_STEPS[currentStepIndex];
  const currentQuestion = currentStep?.questions[currentQuestionIndex];
  
  // Token auth
  const getAuthToken = () => {
    return localStorage.getItem('social_token') || localStorage.getItem('auth_token');
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SAUVEGARDE ET REPRISE DE PROGRESSION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const STORAGE_KEY = 'wizard_abra_progress';
  
  // Sauvegarder la progression à chaque changement
  useEffect(() => {
    // Ne pas sauvegarder si on est à l'étape finale ou au début
    if (currentStepIndex === 0 && currentQuestionIndex === 0) return;
    if (currentStep?.id === 'finish') return;
    
    const progress = {
      stepIndex: currentStepIndex,
      questionIndex: currentQuestionIndex,
      data: collectedData,
      logoPreview: logoPreview,
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [currentStepIndex, currentQuestionIndex, collectedData, logoPreview]);
  
  // Vérifier s'il y a une progression sauvegardée au démarrage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        // Vérifier que la sauvegarde n'est pas trop vieille (7 jours max)
        const savedAt = new Date(progress.savedAt);
        const now = new Date();
        const diffDays = (now - savedAt) / (1000 * 60 * 60 * 24);
        
        if (diffDays < 7 && progress.stepIndex > 0) {
          setSavedProgress(progress);
          setShowResumeModal(true);
        } else {
          // Trop vieux, on efface
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);
  
  // Reprendre la progression sauvegardée
  const resumeProgress = () => {
    if (savedProgress) {
      setCurrentStepIndex(savedProgress.stepIndex);
      setCurrentQuestionIndex(savedProgress.questionIndex);
      setCollectedData(savedProgress.data);
      if (savedProgress.logoPreview) {
        setLogoPreview(savedProgress.logoPreview);
      }
      
      // Construire le message de reprise
      const step = WIZARD_STEPS[savedProgress.stepIndex];
      const question = step?.questions[savedProgress.questionIndex];
      
      setMessages([
        { role: 'assistant', content: `Content de te revoir ! Tu en étais à l'étape "${step?.title}". On continue ?` },
        { role: 'assistant', content: question?.text, type: question?.type, choices: question?.choices }
      ]);
      
      if (question?.type === 'choice') {
        setShowChoices(true);
      }
    }
    setShowResumeModal(false);
  };
  
  // Recommencer depuis le début
  const startFresh = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedProgress(null);
    setShowResumeModal(false);
    
    // Démarrer normalement
    const firstQuestion = WIZARD_STEPS[0].questions[0];
    setMessages([{
      role: 'assistant',
      content: firstQuestion.text,
      type: firstQuestion.type,
      choices: firstQuestion.choices
    }]);
  };
  
  // Effacer la progression quand le wizard est terminé
  const clearProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
  };
  
  // Scroll auto
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Démarrage du wizard (seulement si pas de modal de reprise)
  useEffect(() => {
    if (showResumeModal) return; // Attendre la décision de l'utilisateur
    if (messages.length === 0 && currentQuestion && !savedProgress) {
      setTimeout(() => {
        setMessages([{
          role: 'assistant',
          content: currentQuestion.text,
          type: currentQuestion.type,
          choices: currentQuestion.choices
        }]);
        if (currentQuestion.type === 'choice') {
          setShowChoices(true);
        }
      }, 500);
    }
  }, [showResumeModal]);
  
  // Progression calculée
  const totalQuestions = WIZARD_STEPS.reduce((acc, step) => acc + step.questions.length, 0);
  const answeredQuestions = WIZARD_STEPS.slice(0, currentStepIndex).reduce((acc, step) => acc + step.questions.length, 0) + currentQuestionIndex;
  const progress = (answeredQuestions / totalQuestions) * 100;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES RÉPONSES
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Gestion de l'upload du logo
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Créer une prévisualisation locale
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Upload vers le backend
      const formData = new FormData();
      formData.append('file', file);
      
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const logoUrl = data.url || data.file_url;
        
        setCollectedData(prev => ({ ...prev, logo_url: logoUrl }));
        toast.success('Logo uploadé !');
        
        // Passer à la question suivante
        setTimeout(() => {
          handleSubmit('Logo ajouté');
        }, 500);
      } else {
        // En cas d'erreur, utiliser la prévisualisation locale comme fallback
        toast.warning('Upload en local (sera sauvegardé plus tard)');
        setTimeout(() => {
          handleSubmit('Logo ajouté localement');
        }, 500);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
    }
  };
  
  const skipLogoUpload = () => {
    setMessages(prev => [...prev, { role: 'user', content: 'Je passe cette étape' }]);
    setIsThinking(true);
    setTimeout(() => {
      moveToNextQuestion();
      setIsThinking(false);
    }, 600);
  };
  
  const moveToNextQuestion = () => {
    const nextQuestionIndex = currentQuestionIndex + 1;
    
    if (nextQuestionIndex < currentStep.questions.length) {
      setCurrentQuestionIndex(nextQuestionIndex);
      const nextQuestion = currentStep.questions[nextQuestionIndex];
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: nextQuestion.text,
        type: nextQuestion.type,
        choices: nextQuestion.choices
      }]);
      if (nextQuestion.type === 'choice') {
        setShowChoices(true);
      }
    } else {
      // Passer à l'étape suivante
      goToNextStep();
    }
  };
  
  const goToNextStep = async () => {
    const nextStepIndex = currentStepIndex + 1;
    
    if (nextStepIndex < WIZARD_STEPS.length) {
      setCurrentStepIndex(nextStepIndex);
      setCurrentQuestionIndex(0);
      const nextStep = WIZARD_STEPS[nextStepIndex];
      const nextQuestion = nextStep.questions[0];
      
      // Message de transition
      if (nextStep.category !== 'intro' && nextStep.category !== 'fin') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Parfait ! Passons à ${nextStep.title.toLowerCase()}...`,
          type: 'transition',
          stepInfo: { icon: nextStep.icon, title: nextStep.title, color: nextStep.color }
        }]);
        
        await new Promise(resolve => setTimeout(resolve, 800));
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: nextQuestion.text,
        type: nextQuestion.type,
        choices: nextQuestion.choices,
        summary: nextQuestion.type === 'summary' ? collectedData : null
      }]);
      
      if (nextQuestion.type === 'choice') {
        setShowChoices(true);
      }
      
      // Si c'est la fin, sauvegarder
      if (nextStep.id === 'finish') {
        await saveAllData();
      }
    }
  };
  
  const handleSubmit = async (value) => {
    if ((!value || !value.trim()) && currentQuestion?.type !== 'confirm' && currentQuestion?.type !== 'image_upload') return;
    if (isThinking) return;
    
    const userValue = value?.trim() || 'Oui';
    
    // Ajouter le message utilisateur
    setMessages(prev => [...prev, { role: 'user', content: userValue }]);
    setInputValue('');
    setShowChoices(false);
    setIsThinking(true);
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Validation si nécessaire
    if (currentQuestion?.validate) {
      const parsedValue = currentQuestion.parse ? currentQuestion.parse(userValue) : userValue;
      if (!currentQuestion.validate(parsedValue)) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: currentQuestion.errorMsg || "Je n'ai pas compris, peux-tu reformuler ?",
          type: 'text'
        }]);
        setIsThinking(false);
        return;
      }
    }
    
    // Sauvegarder la valeur
    if (currentQuestion?.field) {
      const parsedValue = currentQuestion.parse ? currentQuestion.parse(userValue) : userValue;
      setCollectedData(prev => ({
        ...prev,
        [currentQuestion.field]: parsedValue
      }));
    }
    
    // Passer à la question suivante
    const nextQuestionIndex = currentQuestionIndex + 1;
    
    if (nextQuestionIndex < currentStep.questions.length) {
      // Prochaine question dans la même étape
      setCurrentQuestionIndex(nextQuestionIndex);
      const nextQuestion = currentStep.questions[nextQuestionIndex];
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: nextQuestion.text,
        type: nextQuestion.type,
        choices: nextQuestion.choices
      }]);
      if (nextQuestion.type === 'choice') {
        setShowChoices(true);
      }
    } else {
      // Passer à l'étape suivante
      const nextStepIndex = currentStepIndex + 1;
      
      if (nextStepIndex < WIZARD_STEPS.length) {
        setCurrentStepIndex(nextStepIndex);
        setCurrentQuestionIndex(0);
        const nextStep = WIZARD_STEPS[nextStepIndex];
        const nextQuestion = nextStep.questions[0];
        
        // Message de transition
        if (nextStep.category !== 'intro' && nextStep.category !== 'fin') {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Parfait ! Passons à ${nextStep.title.toLowerCase()}...`,
            type: 'transition',
            stepInfo: { icon: nextStep.icon, title: nextStep.title, color: nextStep.color }
          }]);
          
          await new Promise(resolve => setTimeout(resolve, 800));
        }
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: nextQuestion.text,
          type: nextQuestion.type,
          choices: nextQuestion.choices,
          summary: nextQuestion.type === 'summary' ? collectedData : null
        }]);
        
        if (nextQuestion.type === 'choice') {
          setShowChoices(true);
        }
        
        // Si c'est la fin, sauvegarder
        if (nextStep.id === 'finish') {
          await saveAllData();
        }
      }
    }
    
    setIsThinking(false);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SAUVEGARDE DES DONNÉES
  // ═══════════════════════════════════════════════════════════════════════════
  
  const saveAllData = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Tu dois être connecté pour sauvegarder');
        return;
      }
      
      // Sauvegarder les infos boutique (step 2)
      const boutiqueData = {
        name: collectedData.name,
        slogan: collectedData.slogan,
        metier_principal: collectedData.metier_principal,
        siret: collectedData.siret,
        address: collectedData.address,
        city: collectedData.city,
        postal_code: collectedData.postal_code,
        phone: collectedData.phone,
        email_pro: collectedData.email_pro,
        zone_intervention_km: collectedData.zone_intervention_km,
        website: collectedData.website,
        instagram: collectedData.instagram,
        facebook: collectedData.facebook,
        description: `${collectedData.metier_principal} - ${collectedData.services_main?.join(', ') || ''}`.slice(0, 500)
      };
      
      // Appel API pour créer/mettre à jour la boutique
      const boutiqueResponse = await fetch(`${API_URL}/api/cockpit/boutique/wizard/step2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(boutiqueData)
      });
      
      if (!boutiqueResponse.ok) {
        console.warn('Boutique save warning:', await boutiqueResponse.text());
      }
      
      // Sauvegarder les mentions légales
      const legalData = {
        payment_terms: collectedData.devis_payment_terms,
        deposit_percent: collectedData.devis_deposit_percent,
        warranty_text: collectedData.devis_warranty_text,
        custom_footer: collectedData.certifications_text
      };
      
      const legalResponse = await fetch(`${API_URL}/api/cockpit/boutique/legal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(legalData)
      });
      
      if (!legalResponse.ok) {
        console.warn('Legal save warning:', await legalResponse.text());
      }
      
      toast.success('Configuration sauvegardée !');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  const isComplete = currentStep?.id === 'finish';
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MODAL DE REPRISE DE PROGRESSION
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (showResumeModal && savedProgress) {
    const savedStep = WIZARD_STEPS[savedProgress.stepIndex];
    const savedDate = new Date(savedProgress.savedAt);
    const formattedDate = savedDate.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Wand2 className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Content de te revoir !</h2>
                <p className="text-white/90 text-sm">Tu avais commencé la configuration</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-2">Ta progression sauvegardée :</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${savedStep?.color} flex items-center justify-center`}>
                  {savedStep?.icon && <savedStep.icon className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{savedStep?.title}</p>
                  <p className="text-xs text-gray-500">Sauvegardé le {formattedDate}</p>
                </div>
              </div>
              
              {/* Données déjà remplies */}
              {savedProgress.data?.name && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Entreprise :</p>
                  <p className="text-sm font-medium text-gray-800">{savedProgress.data.name}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={resumeProgress}
                className="w-full py-3 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <ChevronRight className="w-5 h-5" />
                Reprendre où j'en étais
              </motion.button>
              
              <button
                onClick={startFresh}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Recommencer depuis le début
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Assistant Configuration</h1>
            <p className="text-xs text-white/60">Configuration de ta boutique</p>
          </div>
        </div>
        
        {!isComplete && (
          <button
            onClick={onSkip || (() => navigate('/cockpit-pro'))}
            className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
          >
            Terminer plus tard
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="px-4 py-2 bg-black/20">
        <div className="flex items-center justify-between text-xs text-white/60 mb-1">
          <span>{currentStep?.title}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        {/* Step indicators */}
        <div className="flex justify-between mt-3">
          {WIZARD_STEPS.filter(s => s.category !== 'intro' && s.category !== 'fin').map((step, idx) => {
            const StepIcon = step.icon;
            const stepIndex = WIZARD_STEPS.findIndex(s => s.id === step.id);
            const isActive = currentStepIndex === stepIndex;
            const isPast = currentStepIndex > stepIndex;
            
            return (
              <div key={step.id} className="flex flex-col items-center">
                <div className={`
                  w-8 h-8 rounded-lg flex items-center justify-center transition-all
                  ${isPast ? 'bg-emerald-500' : isActive ? `bg-gradient-to-br ${step.color}` : 'bg-white/10'}
                `}>
                  {isPast ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : (
                    <StepIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-white/40'}`} />
                  )}
                </div>
                <span className={`text-[10px] mt-1 ${isActive ? 'text-white' : 'text-white/40'}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Chat area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence>
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center mr-2 flex-shrink-0">
                  <Wand2 className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div className={`max-w-[80%] ${msg.role === 'user' ? '' : ''}`}>
                {/* Transition step */}
                {msg.type === 'transition' && msg.stepInfo && (
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r ${msg.stepInfo.color} text-white text-sm font-medium mb-2`}
                  >
                    {React.createElement(msg.stepInfo.icon, { className: 'w-4 h-4' })}
                    {msg.stepInfo.title}
                  </motion.div>
                )}
                
                <div className={`
                  px-4 py-3 rounded-2xl
                  ${msg.role === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-br-md'
                    : 'bg-white/10 text-white rounded-bl-md backdrop-blur-sm'
                  }
                `}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  
                  {/* Summary with Boutique Preview */}
                  {msg.type === 'summary' && (
                    <div className="mt-4 space-y-4">
                      {/* Prévisualisation de la boutique */}
                      <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                        {/* Header boutique */}
                        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] p-4">
                          <div className="flex items-center gap-4">
                            {/* Logo */}
                            {(logoPreview || collectedData.logo_url) ? (
                              <img 
                                src={logoPreview || collectedData.logo_url} 
                                alt="Logo"
                                className="w-16 h-16 rounded-xl object-cover border-2 border-white/30"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
                                <Building2 className="w-8 h-8 text-white/60" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-white">{collectedData.name || 'Mon Entreprise'}</h3>
                              {collectedData.slogan && (
                                <p className="text-white/80 text-sm">{collectedData.slogan}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white">
                                  {collectedData.metier_principal || 'Artisan'}
                                </span>
                                <span className="flex items-center gap-1 text-amber-300 text-xs">
                                  <Star className="w-3 h-3 fill-current" />
                                  Nouveau
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Infos boutique */}
                        <div className="p-4 space-y-3 text-gray-700">
                          {collectedData.address && (
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {collectedData.address}, {collectedData.postal_code} {collectedData.city}
                            </div>
                          )}
                          {collectedData.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {collectedData.phone}
                            </div>
                          )}
                          {collectedData.email_pro && (
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {collectedData.email_pro}
                            </div>
                          )}
                          {collectedData.services_main?.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-2">
                              {collectedData.services_main.map((service, idx) => (
                                <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs">
                                  {service}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Badge preview */}
                        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center justify-center gap-2 text-amber-700 text-xs">
                          <Eye className="w-3 h-3" />
                          Aperçu de ta boutique
                        </div>
                      </div>
                      
                      {/* Récapitulatif technique */}
                      <div className="grid grid-cols-2 gap-2">
                        <SummaryCard icon={Percent} label="Acompte devis" value={`${collectedData.devis_deposit_percent}%`} />
                        <SummaryCard icon={Shield} label="Garantie" value={collectedData.devis_warranty_text || 'Non renseigné'} />
                      </div>
                      
                      {isSaving ? (
                        <div className="flex items-center justify-center gap-2 py-3 text-amber-400">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Sauvegarde en cours...</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              localStorage.setItem('wizard_abra_completed', 'true');
                              clearProgress(); // Effacer la progression sauvegardée
                              if (onComplete) onComplete(collectedData);
                              else navigate('/cockpit-pro');
                            }}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-medium"
                          >
                            <Rocket className="w-5 h-5" />
                            Accéder à mon Cockpit Pro
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.01 }}
                            onClick={() => window.open('/boutique/preview', '_blank')}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/10 text-white/80 rounded-xl text-sm hover:bg-white/20 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Voir ma boutique publique
                          </motion.button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Thinking indicator */}
        {isThinking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-start gap-2"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-4 h-4 text-white" />
            </div>
            <div className="px-4 py-3 bg-white/10 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input area */}
      {!isComplete && (
        <div className="p-4 border-t border-white/10 bg-black/20">
          {/* Choices buttons */}
          {showChoices && currentQuestion?.choices && (
            <div className="flex flex-wrap gap-2 mb-3">
              {currentQuestion.choices.map((choice, idx) => (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => handleSubmit(choice)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm transition-colors border border-white/10"
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          )}
          
          {/* Text input */}
          {currentQuestion?.type === 'text' && (
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(inputValue)}
                placeholder={currentQuestion.placeholder || 'Ta réponse...'}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                disabled={isThinking}
                autoFocus
              />
              <button
                onClick={() => handleSubmit(inputValue)}
                disabled={!inputValue.trim() || isThinking}
                className="px-4 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          )}
          
          {/* Confirm button */}
          {currentQuestion?.type === 'confirm' && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => handleSubmit('Oui')}
              className="w-full py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <Sparkles className="w-5 h-5" />
              C'est parti !
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          )}
          
          {/* Image upload */}
          {currentQuestion?.type === 'image_upload' && (
            <div className="space-y-4">
              {/* Prévisualisation du logo */}
              {logoPreview && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex justify-center"
                >
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-24 h-24 rounded-2xl object-cover border-2 border-white/20"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div className="flex gap-3">
                {/* Input file caché */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                
                {/* Bouton upload */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex-1 py-4 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      {logoPreview ? 'Changer le logo' : 'Choisir une image'}
                    </>
                  )}
                </motion.button>
                
                {/* Bouton passer */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  onClick={skipLogoUpload}
                  disabled={isUploading}
                  className="px-6 py-4 bg-white/10 text-white/80 rounded-xl font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
                >
                  Passer
                </motion.button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT SUMMARY CARD
// ═══════════════════════════════════════════════════════════════════════════════

function SummaryCard({ icon: Icon, label, value }) {
  if (!value) return null;
  
  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="flex items-center gap-2 text-white/60 text-xs mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className="text-white text-sm font-medium truncate">{value}</p>
    </div>
  );
}
